/**
 * Email Personalization Options Component
 *
 * Displays customer, activity, restaurant, and system variables for insertion.
 * Adapted from SMS PersonalizationOptions for email variable format ({{variable}}).
 *
 * Phase 1, Task 1.2
 * Date: 2025-10-21
 */

import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Info } from 'lucide-react';
import { cn } from '@/lib/utils';
import {
  EMAIL_VARIABLES,
  CATEGORY_LABELS,
  type EmailVariable
} from './types';
import { hasVariable, ensureUrlProtocol } from '@/utils/email/variableHelpers';

interface RestaurantDetails {
  id: string;
  name: string;
  ordering_page_url?: string;
  review_page_url?: string;
  google_review_url?: string;
  facebook_url?: string;
  instagram_url?: string;
}

interface EmailPersonalizationOptionsProps {
  /** Callback when variable is selected */
  onVariableSelect: (variable: string) => void;

  /** Available restaurants for URL selection */
  restaurants?: RestaurantDetails[];

  /** Currently selected restaurant ID */
  selectedRestaurantId?: string;

  /** Callback when restaurant selection changes */
  onRestaurantChange?: (restaurantId: string) => void;

  /** Allow dynamic variables option (resolves at send time) */
  allowDynamicVariables?: boolean;

  /** Current email content for highlighting active variables */
  currentEmailContent?: string;

  /** Disabled state */
  disabled?: boolean;
}

const DYNAMIC_RESTAURANT_ID = 'dynamic_variables';

const EmailPersonalizationOptions: React.FC<EmailPersonalizationOptionsProps> = ({
  onVariableSelect,
  restaurants = [],
  selectedRestaurantId: externalSelectedRestaurantId,
  onRestaurantChange,
  allowDynamicVariables = true,
  currentEmailContent = '',
  disabled = false
}) => {
  // Use external state if provided, otherwise use internal state
  const [internalSelectedRestaurantId, setInternalSelectedRestaurantId] = useState<string>(
    restaurants.length === 1 ? String(restaurants[0].id) : ''
  );

  const selectedRestaurantId = externalSelectedRestaurantId ?? internalSelectedRestaurantId;
  const setSelectedRestaurantId = onRestaurantChange ?? setInternalSelectedRestaurantId;

  const [activeTab, setActiveTab] = useState<string>('customer');

  /**
   * Check if a variable is currently in the email
   */
  const isVariableActive = (variable: string): boolean => {
    return hasVariable(currentEmailContent, variable);
  };

  /**
   * Check if a restaurant URL variable is active
   */
  const isUrlActive = (urlField: keyof RestaurantDetails): boolean => {
    if (!restaurants || restaurants.length === 0) return false;

    // Check if dynamic variables mode is selected
    if (selectedRestaurantId === DYNAMIC_RESTAURANT_ID) {
      // For dynamic mode, check for the dynamic variable
      const dynamicVarMap: Record<string, string> = {
        ordering_page_url: '{{preferredRestaurantOrderingUrl}}',
        review_page_url: '{{preferredRestaurantReviewUrl}}',
        google_review_url: '{{preferredRestaurantGoogleReviewUrl}}',
        facebook_url: '{{preferredRestaurantFacebookUrl}}',
        instagram_url: '{{preferredRestaurantInstagramUrl}}'
      };

      const dynamicVar = dynamicVarMap[urlField];
      return dynamicVar ? hasVariable(currentEmailContent, dynamicVar) : false;
    }

    // For static mode, check if the actual URL is in content
    let restaurant: RestaurantDetails | undefined;

    if (restaurants.length === 1) {
      restaurant = restaurants[0];
    } else if (selectedRestaurantId) {
      restaurant = restaurants.find(r => r.id === selectedRestaurantId);
    }

    if (!restaurant) return false;

    const url = restaurant[urlField];
    return url ? currentEmailContent.includes(url as string) : false;
  };

  /**
   * Handle restaurant URL insertion
   */
  const handleUrlInsert = (urlField: keyof RestaurantDetails) => {
    if (!restaurants || restaurants.length === 0) {
      return;
    }

    // If dynamic variables mode is selected, insert dynamic variable
    if (selectedRestaurantId === DYNAMIC_RESTAURANT_ID) {
      const dynamicVarMap: Record<string, string> = {
        ordering_page_url: '{{preferredRestaurantOrderingUrl}}',
        review_page_url: '{{preferredRestaurantReviewUrl}}',
        google_review_url: '{{preferredRestaurantGoogleReviewUrl}}',
        facebook_url: '{{preferredRestaurantFacebookUrl}}',
        instagram_url: '{{preferredRestaurantInstagramUrl}}'
      };

      const dynamicVar = dynamicVarMap[urlField];
      if (dynamicVar) {
        onVariableSelect(dynamicVar);
      }
      return;
    }

    // Otherwise, insert static URL
    let restaurant: RestaurantDetails | undefined;

    if (restaurants.length === 1) {
      restaurant = restaurants[0];
    } else if (selectedRestaurantId) {
      restaurant = restaurants.find(r => r.id === selectedRestaurantId);
    }

    if (!restaurant) return;

    const url = restaurant[urlField];
    if (url) {
      // Ensure URL has https:// protocol (SMS URLs stored without it)
      const normalizedUrl = ensureUrlProtocol(url as string);
      onVariableSelect(normalizedUrl);
    }
  };

  /**
   * Render a variable button
   */
  const renderVariableButton = (variable: EmailVariable) => {
    const Icon = variable.icon;
    const isActive = isVariableActive(variable.variable);

    return (
      <TooltipProvider key={variable.variable}>
        <Tooltip>
          <TooltipTrigger asChild>
            <Button
              size="sm"
              variant={isActive ? "default" : "outline"}
              onClick={() => onVariableSelect(variable.variable)}
              disabled={disabled}
              className={cn(
                "flex items-center justify-start gap-2 w-full",
                isActive && "bg-primary !text-white [&_span]:!text-white"
              )}
            >
              {Icon && <Icon className={cn("h-4 w-4 flex-shrink-0", isActive && "!text-white !stroke-white")} />}
              <span className="truncate">{variable.name}</span>
              {variable.required && (
                <Badge variant="destructive" className="ml-auto text-xs">
                  Required
                </Badge>
              )}
            </Button>
          </TooltipTrigger>
          <TooltipContent side="right" className="max-w-xs">
            <div className="space-y-1">
              <p className="font-medium">{variable.description}</p>
              {variable.example && (
                <p className="text-xs text-muted-foreground">
                  Example: <code>{variable.example}</code>
                </p>
              )}
              {variable.note && (
                <p className="text-xs text-muted-foreground italic">
                  {variable.note}
                </p>
              )}
              <p className="text-xs text-muted-foreground">
                Variable: <code>{variable.variable}</code>
              </p>
            </div>
          </TooltipContent>
        </Tooltip>
      </TooltipProvider>
    );
  };

  const showRestaurantSelector = restaurants.length > 1;

  return (
    <div className="space-y-4">
      <div>
        <Label className="text-base font-semibold">Personalization Variables</Label>
        <p className="text-sm text-muted-foreground mt-1">
          Click a variable to insert it into your email
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className="grid w-full grid-cols-3">
          <TabsTrigger value="customer" className="data-[state=active]:text-white">
            Customer Info
          </TabsTrigger>
          <TabsTrigger value="restaurant" className="data-[state=active]:text-white">
            {CATEGORY_LABELS.restaurant}
          </TabsTrigger>
          <TabsTrigger value="system" className="data-[state=active]:text-white">
            {CATEGORY_LABELS.system}
          </TabsTrigger>
        </TabsList>

        {/* Customer & Activity Variables */}
        <TabsContent value="customer" className="space-y-3 mt-4">
          <div>
            <Label className="text-sm font-medium">Customer Info</Label>
            <div className="grid grid-cols-1 gap-2 mt-2">
              {EMAIL_VARIABLES.customer.map(renderVariableButton)}
              {EMAIL_VARIABLES.activity.map(renderVariableButton)}
            </div>
          </div>
        </TabsContent>

        {/* Restaurant Variables */}
        <TabsContent value="restaurant" className="space-y-3 mt-4">
          {/* Restaurant Selector */}
          {showRestaurantSelector && (
            <div>
              <Label className="text-sm text-muted-foreground mb-2 block">
                Select a restaurant to use its information:
              </Label>
              <Select value={selectedRestaurantId} onValueChange={setSelectedRestaurantId}>
                <SelectTrigger className="w-full">
                  <SelectValue placeholder="Select a restaurant" />
                </SelectTrigger>
                <SelectContent>
                  {allowDynamicVariables && (
                    <SelectItem key={DYNAMIC_RESTAURANT_ID} value={DYNAMIC_RESTAURANT_ID}>
                      <div className="flex items-center gap-2">
                        <span>Dynamic Variables</span>
                        <Badge variant="secondary" className="text-xs">
                          Recommended
                        </Badge>
                      </div>
                    </SelectItem>
                  )}
                  {restaurants.map(restaurant => (
                    <SelectItem key={restaurant.id} value={String(restaurant.id)}>
                      {restaurant.name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          {/* Static Restaurant Info */}
          <div>
            <Label className="text-sm font-medium">Restaurant Branding</Label>
            <div className="grid grid-cols-1 gap-2 mt-2">
              {EMAIL_VARIABLES.restaurant.map(renderVariableButton)}
            </div>
          </div>

          {/* Restaurant URLs - Show based on selection */}
          {selectedRestaurantId === DYNAMIC_RESTAURANT_ID ? (
            // Dynamic Variables Mode
            <div>
              <Label className="text-sm font-medium">Dynamic Restaurant URLs</Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                {EMAIL_VARIABLES.dynamic.map(renderVariableButton)}
              </div>

              <Alert className="mt-3">
                <Info className="h-4 w-4" />
                <AlertDescription className="text-xs">
                  <strong>Dynamic variables</strong> automatically resolve to each customer's
                  preferred restaurant at send time using a 3-tier fallback system.
                </AlertDescription>
              </Alert>
            </div>
          ) : (
            // Static URLs Mode (or single restaurant)
            <div>
              <Label className="text-sm font-medium">Restaurant URLs</Label>
              <div className="grid grid-cols-1 gap-2 mt-2">
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant={isUrlActive('ordering_page_url') ? "default" : "outline"}
                        onClick={() => handleUrlInsert('ordering_page_url')}
                        disabled={disabled}
                        className={cn(
                          "flex items-center justify-start gap-2",
                          isUrlActive('ordering_page_url') && "!text-white [&_span]:!text-white"
                        )}
                      >
                        <span className="truncate">Ordering Page URL</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Link to restaurant's ordering page
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant={isUrlActive('review_page_url') ? "default" : "outline"}
                        onClick={() => handleUrlInsert('review_page_url')}
                        disabled={disabled}
                        className={cn(
                          "flex items-center justify-start gap-2",
                          isUrlActive('review_page_url') && "!text-white [&_span]:!text-white"
                        )}
                      >
                        <span className="truncate">Review Page URL</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Link to leave a review
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant={isUrlActive('google_review_url') ? "default" : "outline"}
                        onClick={() => handleUrlInsert('google_review_url')}
                        disabled={disabled}
                        className={cn(
                          "flex items-center justify-start gap-2",
                          isUrlActive('google_review_url') && "!text-white [&_span]:!text-white"
                        )}
                      >
                        <span className="truncate">Google Review URL</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Google Business review link
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant={isUrlActive('facebook_url') ? "default" : "outline"}
                        onClick={() => handleUrlInsert('facebook_url')}
                        disabled={disabled}
                        className={cn(
                          "flex items-center justify-start gap-2",
                          isUrlActive('facebook_url') && "!text-white [&_span]:!text-white"
                        )}
                      >
                        <span className="truncate">Facebook URL</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Facebook page link
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>

                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button
                        size="sm"
                        variant={isUrlActive('instagram_url') ? "default" : "outline"}
                        onClick={() => handleUrlInsert('instagram_url')}
                        disabled={disabled}
                        className={cn(
                          "flex items-center justify-start gap-2",
                          isUrlActive('instagram_url') && "!text-white [&_span]:!text-white"
                        )}
                      >
                        <span className="truncate">Instagram URL</span>
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent side="right">
                      Instagram profile link
                    </TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>

              {showRestaurantSelector && selectedRestaurantId && (
                <Alert className="mt-3">
                  <Info className="h-4 w-4" />
                  <AlertDescription className="text-xs">
                    URLs will be inserted from the selected restaurant.
                    Switch to <strong>Dynamic Variables</strong> to automatically use each
                    customer's preferred restaurant.
                  </AlertDescription>
                </Alert>
              )}
            </div>
          )}
        </TabsContent>

        {/* System Variables */}
        <TabsContent value="system" className="space-y-3 mt-4">
          <div>
            <Label className="text-sm font-medium">System Variables</Label>
            <div className="grid grid-cols-1 gap-2 mt-2">
              {EMAIL_VARIABLES.system.map(renderVariableButton)}
            </div>
          </div>

          <Alert>
            <Info className="h-4 w-4" />
            <AlertDescription className="text-xs">
              <code className="font-semibold">{'{{unsubscribeUrl}}'}</code> is required for
              CAN-SPAM compliance and will be validated before sending.
            </AlertDescription>
          </Alert>
        </TabsContent>
      </Tabs>
    </div>
  );
};

export default EmailPersonalizationOptions;
